import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { TRACKS, COMPLIANCE_DASH, COMPLIANCE_DASH_LINKS, COMPLIANCE_DASH_NOTE, SPECIALTY_ENGINES, ADMIN_OFFLOAD } from "../../data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { SpecialtyDrawer } from "./SpecialtyDrawer";

const RoiCalculator = () => {
  const [encounters, setEncounters] = useState(4000);
  const revenue = Math.round(encounters * 207.72);
  return (
    <div className="mt-8 bg-white border border-[#E2E8F0] rounded-xl p-8 shadow-xl" data-testid="roi-calculator">
      <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Interactive ROI & PPS Revenue Calculator</p>
      <div className="mt-5 lg:grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <label className="text-sm font-medium text-[#0B1B2B]">Additional annual pod encounters: <span className="font-serif text-lg font-bold">{encounters.toLocaleString()}</span></label>
          <input
            type="range" min="500" max="20000" step="250" value={encounters}
            onChange={(e) => setEncounters(Number(e.target.value))}
            className="mt-3 w-full accent-[#D4AF37]"
            data-testid="roi-slider"
          />
          <p className="mt-2 text-xs text-[#64748B]">Modeled on the 2026 CMS PPS base encounter rate ($207.72). Faculty preceptors bill as providers of record — your organization retains the full encounter value. Projected, not a live client result.</p>
        </div>
        <div className="lg:col-span-5 mt-6 lg:mt-0 bg-[#0B1B2B] rounded-xl p-6 text-center">
          <div className="text-xs uppercase tracking-[0.15em] text-[#D4AF37]">Annual PPS Revenue Retained</div>
          <div className="mt-2 font-serif text-3xl md:text-4xl text-white font-bold" data-testid="roi-revenue">${revenue.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
};

const Icon = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
};

export const Pathways = ({ onBeginIntake, tracks = TRACKS, gated = false }) => {
  const [active, setActive] = useState(tracks[0].id);
  const [drawer, setDrawer] = useState({ open: false, specialty: null });
  const track = tracks.find((t) => t.id === active) || tracks[0];

  return (
    <section id="gateways" className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="pathways-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">
          {gated ? "Subscriber Operational Engines" : "Core Stakeholder Pathways"}
        </p>
        <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1B2B] tracking-tight max-w-3xl">
          {gated ? "Your Provisioned Operational Pathways." : "One Infrastructure. Seven Institutional Audiences."}
        </h2>

        <div className="mt-12 flex flex-wrap gap-1 border-b border-[#E2E8F0]" role="tablist">
          {tracks.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={active === t.id}
              onClick={() => setActive(t.id)}
              className={`px-5 py-4 font-sans text-sm font-semibold transition-colors relative ${
                active === t.id ? "text-[#0B1B2B]" : "text-[#64748B] hover:text-[#0B1B2B]"
              }`}
              data-testid={`pathway-tab-${t.id}`}
            >
              {t.tab}
              {active === t.id && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#D4AF37]" />}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={track.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="pt-12"
            data-testid={`pathway-content-${track.id}`}
          >
            <div className="lg:grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-7">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">{track.overline}</p>
                <h3 className="mt-3 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">{track.headline}</h3>
                <p className="mt-5 text-base md:text-lg text-[#64748B] leading-relaxed">{track.description}</p>
                <button
                  onClick={() => onBeginIntake(track.id)}
                  className="mt-8 bg-[#0B1B2B] text-white font-medium px-7 py-3.5 rounded-xl hover:bg-[#152a40] hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2 group"
                  data-testid={`begin-intake-${track.id}`}
                >
                  {track.cta || "Begin Intake"}
                  <Icons.ArrowRight size={16} className="text-[#D4AF37] transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
              <div className="lg:col-span-5 mt-10 lg:mt-0">
                <div className="rounded-xl overflow-hidden shadow-xl border border-[#E2E8F0] h-64 lg:h-full max-h-80">
                  <img src={track.image} alt={track.tab} className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {track.offerings.map((o) => (
                <div key={o.title} className="bg-white border border-[#E2E8F0] shadow-xl rounded-xl p-7 transition-all duration-300 hover:shadow-2xl hover:border-[#D4AF37]/50">
                  <div className="w-11 h-11 rounded-xl bg-[#0B1B2B] flex items-center justify-center">
                    <Icon name={o.icon} size={20} className="text-[#D4AF37]" />
                  </div>
                  <h4 className="mt-5 font-serif text-xl text-[#0B1B2B] font-semibold">{o.title}</h4>
                  <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{o.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-[#0B1B2B] rounded-xl p-8 md:p-10">
              <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Systems & Standards Alignment Dashboard</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {track.systems.map((s) => (
                  <span key={s} className="px-4 py-2 rounded-lg border border-[#D4AF37]/40 text-white/90 text-sm font-medium bg-white/5">
                    {s}
                  </span>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap gap-2">
                {COMPLIANCE_DASH.map((c) => (
                  COMPLIANCE_DASH_LINKS[c] ? (
                    <a key={c} href={COMPLIANCE_DASH_LINKS[c]} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-semibold tracking-wide hover:bg-[#D4AF37]/25 transition-colors">
                      {c}
                    </a>
                  ) : (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-semibold tracking-wide">
                      {c}
                    </span>
                  )
                ))}
              </div>
              <p className="mt-3 text-[11px] text-white/40 leading-relaxed max-w-2xl">{COMPLIANCE_DASH_NOTE}</p>
            </div>

            {track.roi && <RoiCalculator />}

            {SPECIALTY_ENGINES[track.id] && (
              <div className="mt-12" data-testid={`specialty-engines-${track.id}`}>
                <h4 className="font-serif text-xl md:text-2xl text-[#0B1B2B]">Track-Specific Operational Engines & Compliance</h4>
                <p className="mt-2 text-sm text-[#64748B] max-w-3xl leading-relaxed">
                  CCNE / CNE preceptor requirements, clinical hour thresholds, and New Jersey private-practice
                  compliance standards — enforced per specialty engine.
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {SPECIALTY_ENGINES[track.id].map((eng) => (
                    <div key={eng.name} className="bg-white border border-[#E2E8F0] rounded-xl p-6 hover:border-[#D4AF37]/50 hover:shadow-lg transition-all duration-300" data-testid={`engine-${eng.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <div className="flex items-center justify-between">
                        <h5 className="font-serif text-lg text-[#0B1B2B] font-semibold">{eng.name}</h5>
                        <span className="text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full bg-[#0B1B2B] text-[#D4AF37]">{eng.cert}</span>
                      </div>
                      <ul className="mt-4 space-y-2">
                        {eng.items.map((it) => (
                          <li key={it} className="flex items-start gap-2 text-xs text-[#64748B] leading-snug">
                            <Icons.ShieldCheck size={13} className="text-[#D4AF37] mt-0.5 shrink-0" /> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(track.id === "universities" || track.id === "lpn") && (
              <div className="mt-10 bg-[#0B1B2B] rounded-xl p-8 md:p-10" data-testid={`admin-offload-${track.id}`}>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">{ADMIN_OFFLOAD.title}</p>
                <p className="mt-3 text-slate-300 text-sm md:text-base leading-relaxed max-w-3xl">{ADMIN_OFFLOAD.desc}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {ADMIN_OFFLOAD.items.map((it) => (
                    <span key={it} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D4AF37]/40 text-white/90 text-sm font-medium bg-white/5">
                      <Icons.MonitorCheck size={14} className="text-[#D4AF37]" /> {it}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {track.spotlight && (
              <div className="mt-8 border-2 border-[#D4AF37] bg-white rounded-xl p-8 shadow-xl" data-testid="spotlight-card">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Feature Spotlight</p>
                <h4 className="mt-2 font-serif text-xl md:text-2xl text-[#0B1B2B] font-semibold">{track.spotlight.title}</h4>
                <p className="mt-3 text-sm text-[#64748B] leading-relaxed">{track.spotlight.desc}</p>
              </div>
            )}

            {track.support && track.support.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-3" data-testid={`support-provisions-${track.id}`}>
                {track.support.map((s) => (
                  <span key={s} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#E2E8F0] text-sm font-medium text-[#0B1B2B] shadow-sm">
                    <Icons.Building size={15} className="text-[#D4AF37]" /> {s}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-16 lg:grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <h4 className="font-serif text-xl md:text-2xl text-[#0B1B2B]">Specialty Tracks</h4>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {track.specialties.map((sp) => (
                    <button
                      key={sp.name}
                      onClick={() => setDrawer({ open: true, specialty: sp })}
                      className="flex items-center gap-3 p-4 bg-white border border-[#E2E8F0] rounded-xl text-left hover:border-[#D4AF37]/60 hover:shadow-lg transition-all duration-300"
                      data-testid={`specialty-${track.id}-${sp.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <Icon name={sp.icon} size={18} className="text-[#D4AF37] shrink-0" />
                      <span className="text-sm font-medium text-[#0B1B2B]">{sp.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-7 mt-12 lg:mt-0">
                <h4 className="font-serif text-xl md:text-2xl text-[#0B1B2B]">Operational Roadmap</h4>
                <div className="mt-6 space-y-0">
                  {track.roadmap.map((step, i) => (
                    <div key={step} className="flex gap-5">
                      <div className="flex flex-col items-center">
                        <div className="w-9 h-9 rounded-full border-2 border-[#D4AF37] bg-white flex items-center justify-center font-serif font-bold text-[#0B1B2B] text-sm shrink-0">
                          {i + 1}
                        </div>
                        {i < track.roadmap.length - 1 && <div className="w-px flex-1 bg-[#E2E8F0] my-1" />}
                      </div>
                      <div className="pb-8 pt-1.5 text-[#0B1B2B] font-medium">{step}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 max-w-3xl">
              <h4 className="font-serif text-xl md:text-2xl text-[#0B1B2B] mb-4">Executive Briefings</h4>
              <Accordion type="single" collapsible className="bg-white border border-[#E2E8F0] rounded-xl shadow-xl px-6">
                {track.accordion.map((item, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className={i === track.accordion.length - 1 ? "border-b-0" : ""}>
                    <AccordionTrigger className="text-left font-medium text-[#0B1B2B] hover:no-underline hover:text-[#D4AF37]" data-testid={`accordion-${track.id}-${i}`}>
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#64748B] leading-relaxed">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <SpecialtyDrawer
        open={drawer.open}
        onClose={() => setDrawer({ ...drawer, open: false })}
        track={track}
        specialty={drawer.specialty}
        onBeginIntake={onBeginIntake}
      />
    </section>
  );
};
